import { Input } from '@flowus/common/components/input';
import { Tooltip } from '@flowus/common/components/tooltip';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import { type FC } from 'react';
import { Switch } from 'src/common/components/switch';
import { useModel } from 'src/common/create-model';
import { HelperIconBox } from 'src/components/helper-icon';
import { useCurrentSpace, useUpdateSpace } from 'src/hooks/space';
import { isTeamProSpace } from 'src/hooks/space/use-is-pro-space';
import { transaction } from 'src/hooks/use-transaction';
import { SettingModalCommon, SettingProvider } from '../common';
import { Option } from '../option';
import { OpenSettingFrom } from '../type';

export const SpaceSecurity: FC = () => {
  const { t } = useAppTranslation();
  const {
    spaceSettingState: {
      abandonedPublicShare,
      abandonedGuest,
      abandonedExport,
      openWatermark,
      openImageWatermark,
      imageWatermarkText,
      setImageWatermarkText,
    },
  } = useModel(SettingProvider);
  const currentSpace = useCurrentSpace();
  const updateSpace = useUpdateSpace();

  const disabled = !isTeamProSpace(currentSpace.planType);

  return (
    <div className="mt-4">
      <SettingModalCommon.SettingDivider />
      <Option
        showUpgradeDialog={disabled}
        showSplitLine={false}
        showSpacePlan={true}
        from={OpenSettingFrom.space_security}
        upgradeTip={t('common.upgrade_team_version_for_security_options')}
        title={
          <HelperIconBox
            showBuildin
            iconClassName="pointer-events-none"
            popup={t('common.upgrade_team_version_for_security_options')}
          >
            {t('common.security_options')}
          </HelperIconBox>
        }
        titleClassName="text-t1-medium text-red"
      />
      <Option
        className="mt-0"
        showSplitLine={false}
        title={t('common.disable_page_public_share')}
        description={t('common.disable_page_public_share_desc')}
        rightPart={
          <Tooltip popup={t('admin_only_setting')} delay={1500}>
            <Switch
              disabled={disabled}
              open={abandonedPublicShare}
              onSwitch={(open) => {
                transaction(() => {
                  updateSpace(currentSpace.uuid, {
                    setting: {
                      ...currentSpace.setting,
                      abandonedPublicShare: open,
                    },
                  });
                });
              }}
            />
          </Tooltip>
        }
      />
      <Option
        showSplitLine={false}
        title={t('common.disable_external_collaborators')}
        description={t('common.disable_external_collaborators_desc')}
        rightPart={
          <Tooltip popup={t('admin_only_setting')} delay={1500}>
            <Switch
              disabled={disabled}
              open={abandonedGuest}
              onSwitch={(open) => {
                transaction(() => {
                  updateSpace(currentSpace.uuid, {
                    setting: {
                      ...currentSpace.setting,
                      abandonedGuest: open,
                    },
                  });
                });
              }}
            />
          </Tooltip>
        }
      />
      <Option
        showSplitLine={false}
        title={t('common.disable_export')}
        description={t('common.disable_export_desc')}
        rightPart={
          <Tooltip popup={t('admin_only_setting')} delay={1500}>
            <Switch
              disabled={disabled}
              open={abandonedExport}
              onSwitch={(open) => {
                transaction(() => {
                  updateSpace(currentSpace.uuid, {
                    setting: {
                      ...currentSpace.setting,
                      abandonedExport: open,
                    },
                  });
                });
              }}
            />
          </Tooltip>
        }
      />
      <Option
        showSplitLine={false}
        title={t('common.space_watermark')}
        description={t('common.space_watermark_desc')}
        rightPart={
          <Tooltip popup={t('admin_only_setting')} delay={1500}>
            <Switch
              disabled={disabled}
              open={openWatermark}
              onSwitch={(open) => {
                transaction(() => {
                  updateSpace(currentSpace.uuid, {
                    setting: {
                      ...currentSpace.setting,
                      openWatermark: open,
                    },
                  });
                });
              }}
            />
          </Tooltip>
        }
      />
      {!__BUILD_IN__ && (
        <Option
          showSplitLine={false}
          title={t('common.image_watermark')}
          description={
            <span>
              {t('common.image_watermark_desc')}
              <span className="text-black text-t3-medium">{t('common.share_page')}</span>
              {t('common.image_watermark_desc_suffix')}
              {openImageWatermark && (
                <Input
                  focusWidthBorder={false}
                  maxLength={20}
                  placeholder={t('common.input_watermark_text')}
                  className="w-full h-7 mt-2 "
                  inputClassName="text-grey3"
                  value={imageWatermarkText}
                  onChange={(str) => {
                    setImageWatermarkText(str);
                  }}
                />
              )}
            </span>
          }
          rightPart={
            <Tooltip popup={t('admin_only_setting')} delay={1500}>
              <Switch
                disabled={disabled}
                open={openImageWatermark}
                onSwitch={(open) => {
                  transaction(() => {
                    updateSpace(currentSpace.uuid, {
                      setting: {
                        ...currentSpace.setting,
                        openImageWatermark: open,
                      },
                    });
                  });
                }}
              />
            </Tooltip>
          }
        />
      )}
    </div>
  );
};
